<template>
  <div>
    <b-row>
      <b-col>
        <b-row>
          <b-col>
            <p class="mt-2">
              <label>
                <span class="mr-2">
                  {{ $t('email-tester.recipients-label') }}
                </span>
                <b-form-input
                  v-model="recipients"
                  :placeholder="$t('email-tester.recipients-placeholder')"
                />
              </label>
            </p>
          </b-col>
          <b-col>
            <label class="mr-2">
              {{ $t('email-tester.job-search-label') }}
            </label>
            <b-form-input
              v-model="searchJobId"
              name="searchJobId"
              :placeholder="$t('email-tester.job-search-placeholder')"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row v-if="selectedJob" class="m-4">
      <b-col>
        <span class="mr-2">{{ $t('email-tester.job-number') }}</span>
        {{ selectedJob.job.name }}
        <br />
        <span class="mr-2">{{ $t('email-tester.partner') }}</span>
        <template v-if="selectedJob.partner && selectedJob.partner.id">
          {{ selectedJob.partner.name }} [{{ selectedJob.partner.id }}]
        </template>
        <b-badge v-else variant="warning">
          {{ $t('email-tester.no-partner') }}
        </b-badge>
        <br />
        <span class="mr-2">{{ $t('email-tester.brand') }}</span>
        <template v-if="selectedJob.brand && selectedJob.brand.name">
          {{ getBrandShortName(selectedJob.brand) }}
        </template>
        <b-badge v-else variant="warning">
          {{ $t('email-tester.no-brand') }}
        </b-badge>
        <br />
        <span class="mr-2">{{ $t('email-tester.customer') }}</span>
        <template v-if="selectedJob.customer && selectedJob.customer.id">
          {{ selectedJob.customer.name || selectedJob.customer.email }}
          [{{ selectedJob.customer.id }}]
        </template>
        <b-badge v-else variant="warning">
          {{ $t('email-tester.no-customer') }}
        </b-badge>
        <br />
        <property-object :value="links" :clickable-links="true" />
      </b-col>
    </b-row>

    <div>
      <h5>{{ $t('email-tester.select-email-type') }}</h5>
      <multiselect
        :multiple="false"
        v-model="selectedTemplate"
        :options="templates"
        track-by="key"
        :custom-label="templateName"
        group-label="group"
        group-values="options"
      >
        <template slot="singleLabel" slot-scope="props">
          <span>
            <code>{{ props.option.key }}</code>
            -
            {{ props.option.description }}
          </span>
        </template>
        <template slot="option" slot-scope="props">
          <span v-if="props.option.$groupLabel">
            <b>{{ props.option.$groupLabel }}</b>
          </span>
          <span v-else>
            <code>{{ props.option.key }}</code>
            - {{ props.option.description }}
          </span>
        </template>
      </multiselect>
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('email-tester.template-revision')"
            class="mt-2"
          >
            <b-form-select
              v-model="revisionSelected"
              :options="revisionOptions"
            >
              <template #first>
                <b-form-select-option :value="null">
                  {{ $t('email-tester.automatic') }}
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            :label="$t('email-tester.template-locale')"
            class="mt-2"
          >
            <b-form-select v-model="localeSelected" :options="locales">
              <template #first>
                <b-form-select-option :value="null">
                  {{ $t('email-tester.automatic') }}
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <div v-if="selectedTemplate" class="m-4">
        <div class="row">
          <div class="col text-center">
            <h5>{{ $t('email-tester.required-input') }}</h5>
          </div>
        </div>
        <b-alert v-if="missingArgsWarning" show variant="warning" class="mb-3">
          {{ missingArgsWarning }}
        </b-alert>
        <div class="d-flex flex-wrap">
          <div
            class="form-group mr-2"
            v-for="(argument, index) in selectedTemplate.args"
            :key="index"
          >
            <label>
              {{ argument.label }}
              <b-badge v-if="argument.value" variant="success" class="ml-1">
                {{ $t('email-tester.auto-filled') }}
              </b-badge>
              <b-badge v-else variant="danger" class="ml-1">
                {{ $t('email-tester.required') }}
              </b-badge>
            </label>
            <input
              type="text"
              class="form-control mb-2"
              :class="{ 'border-danger': !argument.value }"
              :placeholder="argumentHint(argument.label)"
              v-model="argument.value"
            />
          </div>
        </div>
        <button
          class="btn btn-primary mb-2"
          :disabled="hasEmptyArgs || sending"
          @click.prevent="send"
        >
          {{
            sending ? $t('email-tester.sending') : $t('email-tester.send-email')
          }}
        </button>
        <small v-if="hasEmptyArgs" class="text-danger ml-2">
          {{ $t('email-tester.fill-required') }}
        </small>
      </div>
    </div>
    <messages-list
      :errorMessages="errorMessages"
      :successMessages="successMessages"
    ></messages-list>
  </div>
</template>

<script>
import axios from 'axios';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import PropertyObject from '@gid/vue-common/components/inputs/PropertyObject.vue';
import locales from '../../i18n/locales';
import { getBrandShortName } from '@gid/vue-common/utils';

export default {
  props: {
    jobId: String,
  },
  components: {
    PropertyObject,
  },
  data() {
    return {
      links: null,
      searchJobId: this.jobId,
      selectedJob: null,
      templateData: {},
      templates: [],
      selectedTemplate: null,
      errorMessages: [],
      successMessages: [],
      recipients: this.$store.state.auth.user.contact.email,
      revisionOptions: [],
      revisionSelected: null,
      localeSelected: null,
      locales,
      sending: false,
    };
  },
  computed: {
    hasEmptyArgs() {
      if (!this.selectedTemplate) return false;
      return this.selectedTemplate.args.some((arg) => !arg.value);
    },
    missingArgsWarning() {
      if (!this.selectedTemplate || !this.selectedJob) return null;
      const missing = [];
      for (const arg of this.selectedTemplate.args) {
        if (arg.value) continue;
        const reason = this.getMissingReason(arg.label);
        if (reason) missing.push(reason);
      }
      if (!missing.length) return null;
      return this.$t('email-tester.missing-data-warning', {
        missing: missing.join(', '),
      });
    },
  },
  watch: {
    searchJobId() {
      this.loadJob();
    },
    selectedTemplate() {
      this.fillArgs();
      this.loadVersions();
    },
  },
  methods: {
    getBrandShortName,
    templateName({ key, description }) {
      return `${key} — ${description}`;
    },
    fillArgs() {
      if (this.selectedTemplate) {
        for (let arg of this.selectedTemplate.args) {
          arg.value = this.getInputValue(arg.label);
        }
      }
    },
    loadTemplates() {
      axios.get('/api/admin/test-email/').then((response) => {
        this.templateData = response.data;
        for (let [key, template] of Object.entries(this.templateData)) {
          template.key = key;
          template.args = [];
          for (let index in template.arguments) {
            const argument = template.arguments[index];
            template.args.push({
              label: argument,
              value: '',
            });
          }
          let group = template.key.match(
            /^(customer|partner|brand|accounting)/,
          );
          if (group) {
            group = group[0];
            group = group.charAt(0).toUpperCase() + group.slice(1);
          } else {
            group = 'Misc';
          }
          template.group = group;
        }
        this.templates = Object.values(this.templateData)
          .reduce((options, template) => {
            const group = options.find((t) => t.group === template.group);
            if (group) {
              group.options.push(template);
            } else {
              options.push({
                group: template.group,
                options: [template],
              });
            }
            return options;
          }, [])
          .sort((a, b) => {
            const order = ['Customer', 'Partner', 'Brand'];
            const aIndex = order.indexOf(a.group) || 10000;
            const bIndex = order.indexOf(b.group) || 10000;
            return bIndex - aIndex;
          });
      });
    },
    loadJob() {
      if (this.searchJobId) {
        const params = {
          page_size: 1,
          search: this.searchJobId,
          status: null,
          owner: null,
          include_test_jobs: true,
        };
        return axios
          .get(`/api/admin/jobs`, {
            params,
          })
          .then((response) => {
            this.selectedJob = response.data.data[0];
            this.fillArgs();
            this.loadCustomerAccessLink(this.selectedJob.job.sfid);
          })
          .catch(() => {});
      }
    },
    loadCustomerAccessLink(jobId) {
      return axios
        .get(`/api/admin/test-customer-access-link/${jobId}`)
        .then((response) => {
          this.links = response.data;
          if (location.protocol !== 'https:') {
            for (const key of Object.keys(this.links)) {
              this.links[key] = this.links[key]?.replace(/^https:/i, 'http:');
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
    loadVersions() {
      this.revisionOptions = [];
      this.revisionSelected = null;
      const queryString = RequestQueryBuilder.create({
        filter: [
          {
            field: 'path',
            operator: CondOperator.EQUALS,
            value: `email/${this.selectedTemplate.key}.html`,
          },
        ],
      }).query();

      axios.get(`/data-api/template/?${queryString}`).then((response) => {
        this.revisionOptions = response.data.map(({ id }) => id);
      });
    },
    getInputValue(inputName) {
      if (!this.selectedJob) {
        return null;
      }
      const job = this.selectedJob;
      switch (inputName) {
        case 'Partner SFID':
          return job.partner && job.partner.id ? job.partner.id : null;
        case 'Jobs SFID':
        case 'Order SFID':
        case 'Offer SFID':
          return job.job && job.job.sfid ? job.job.sfid : null;
        case 'Brand Contact SFID':
        case 'Brand Project Manager Brand SFID':
          return job.brand && job.brand.id ? job.brand.id : null;
        case 'Brand Project Manager Contact SFID':
          return job.brand && job.brand.id ? job.brand.id : null;
        case 'Brand Project Manager Order SFID':
          return job.job && job.job.sfid ? job.job.sfid : null;
        case 'Brand Project Manager Order APIID':
          return job.job && job.job.api_id ? job.job.api_id : null;
        case 'Brand Project Manager Partner SFID':
          return job.partner && job.partner.id ? job.partner.id : null;
        case 'User Contact SFID':
          return job.customer && job.customer.id ? job.customer.id : null;
        case 'Document Number':
        case 'Accounting Document ID':
        case 'Job ID':
        case 'JOB ID':
          return null;
      }
      return null;
    },
    getMissingReason(inputName) {
      if (!this.selectedJob) return null;
      const job = this.selectedJob;
      switch (inputName) {
        case 'Partner SFID':
        case 'Brand Project Manager Partner SFID':
          if (!job.partner || !job.partner.id)
            return this.$t('email-tester.missing-reason.no-partner');
          break;
        case 'Brand Contact SFID':
        case 'Brand Project Manager Brand SFID':
        case 'Brand Project Manager Contact SFID':
          if (!job.brand || !job.brand.id)
            return this.$t('email-tester.missing-reason.no-brand');
          break;
        case 'User Contact SFID':
          if (!job.customer || !job.customer.id)
            return this.$t('email-tester.missing-reason.no-customer');
          break;
      }
      return null;
    },
    argumentHint(label) {
      const hints = {
        'Partner SFID': this.$t('email-tester.hints.partner'),
        'Order SFID': this.$t('email-tester.hints.job'),
        'Jobs SFID': this.$t('email-tester.hints.job'),
        'Offer SFID': this.$t('email-tester.hints.offer'),
        'Brand Contact SFID': this.$t('email-tester.hints.brand-contact'),
        'User Contact SFID': this.$t('email-tester.hints.user-contact'),
        'Document Number': this.$t('email-tester.hints.document-number'),
        'Accounting Document ID': this.$t(
          'email-tester.hints.accounting-document',
        ),
        'Job ID': this.$t('email-tester.hints.job'),
        'JOB ID': this.$t('email-tester.hints.job'),
        'Brand Project Manager Brand SFID': this.$t('email-tester.hints.brand'),
        'Brand Project Manager Contact SFID': this.$t(
          'email-tester.hints.brand-contact',
        ),
        'Brand Project Manager Order SFID': this.$t('email-tester.hints.order'),
        'Brand Project Manager Order APIID': this.$t(
          'email-tester.hints.order-api-id',
        ),
        'Brand Project Manager Partner SFID': this.$t(
          'email-tester.hints.partner',
        ),
      };
      return hints[label] || label;
    },
    send() {
      const template_id = this.selectedTemplate.key;
      const parameters = this.selectedTemplate.args;
      const args = [];
      for (let index in parameters) {
        args.push(parameters[index].value);
      }
      this.sending = true;
      axios
        .post('/api/admin/test-email/', {
          template_name: template_id,
          template_revision: this.revisionSelected,
          locale: this.localeSelected,
          arguments: args,
          recipients: this.recipients,
        })
        .then(() => {
          this.successMessages.push(
            this.$t('email-tester.send-success', { template: template_id }),
          );
        })
        .catch((error) => {
          const raw =
            error.response && error.response.data
              ? error.response.data.errors || error.response.data
              : error.message;
          this.errorMessages.push(this.formatError(template_id, raw));
        })
        .finally(() => {
          this.sending = false;
        });
    },
    formatError(templateKey, raw) {
      const msg = typeof raw === 'string' ? raw : JSON.stringify(raw);
      if (msg.includes('index out of range') || msg.includes('IndexError')) {
        return this.$t('email-tester.errors.missing-argument', {
          template: templateKey,
        });
      }
      if (msg.includes('NoneType') || msg.includes("'NoneType'")) {
        const needs = this.selectedTemplate.args.map((a) => a.label).join(', ');
        return this.$t('email-tester.errors.incomplete-data', {
          template: templateKey,
          needs,
        });
      }
      if (msg.includes('not found') || msg.includes('404')) {
        return this.$t('email-tester.errors.not-found', {
          template: templateKey,
        });
      }
      if (msg.includes('template') && msg.includes('not found')) {
        return this.$t('email-tester.errors.template-not-found', {
          template: templateKey,
        });
      }
      return this.$t('email-tester.errors.generic', {
        template: templateKey,
        message: msg,
      });
    },
  },
  created() {
    this.loadTemplates();
  },
};
</script>
